import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ContentCut as ContentCutIcon,
  ExpandMore as ExpandMoreIcon,
  Functions as FunctionsIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Sync as SyncIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import axios from '../utils/axios';
import { PageHeader, StatCard } from '../components/ui';
import DateRangeField from '../components/ui/DateRangeField';
import { useIsMobile } from '../utils/responsive';
import {
  NO_COLOR,
  applyItemFilters,
  buildQuery,
  colorFilterKey,
  fmtInt,
  fmtKg,
  fmtMoney,
  fmtQty,
  hasAnyColor,
  longDate,
  parseQuery,
  quickRange,
  resolveQuery,
  shortDate,
  trendLabelVisible,
} from './productionAnalyticsUi.mjs';

// ============================================================
// Константы и хелперы
// ============================================================

const QUICK_RANGES = [
  { kind: 'today', label: 'Сегодня' },
  { kind: 'yesterday', label: 'Вчера' },
  { kind: '7d', label: '7 дней' },
  { kind: '30d', label: '30 дней' },
  { kind: 'month', label: 'Месяц' },
];

const TOTAL_KPI_KEYS = new Set(['total_ops', 'total_sewing']);

// Акценты процессов — только варианты темы, по кругу (KPI, тренд и секция одного процесса совпадают)
const TONES = ['primary', 'secondary', 'info', 'success', 'warning'];

function toneFor(index) {
  return TONES[((Number(index) || 0) % TONES.length + TONES.length) % TONES.length];
}

/** Акцент по имени процесса — порядок KPI задаёт цвет, секции и тренды его переиспользуют. */
function buildToneMap(kpis) {
  const map = new Map();
  let idx = 0;
  for (const kpi of Array.isArray(kpis) ? kpis : []) {
    if (TOTAL_KPI_KEYS.has(kpi.key)) continue;
    map.set(kpi.key, toneFor(idx));
    if (kpi.label) map.set(kpi.label, toneFor(idx));
    idx += 1;
  }
  return map;
}

function errMessage(err) {
  return err?.response?.data?.message || err?.message || 'Не удалось загрузить данные';
}

function isAbort(err) {
  return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
}

function uniqueModels(items) {
  const seen = new Set();
  const list = [];
  for (const it of items || []) {
    const model = it?.model;
    if (model == null || model === '' || seen.has(model)) continue;
    seen.add(model);
    list.push(model);
  }
  return list;
}

function uniqueColors(items) {
  const set = new Set();
  for (const it of items || []) {
    for (const row of it?.by_color || []) set.add(colorFilterKey(row?.color));
  }
  return [...set].sort((a, b) => {
    if (a === NO_COLOR) return -1;
    if (b === NO_COLOR) return 1;
    return a.localeCompare(b, 'ru');
  });
}

function colorLabel(key) {
  return key === NO_COLOR ? 'без цвета' : key;
}

function formatSync(iso) {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD.MM HH:mm') : String(iso);
}

function formatRange(from, to) {
  if (!from || !to) return '';
  if (from === to) return longDate(from);
  return `${longDate(from)} — ${longDate(to)}`;
}

function activeQuick(from, to) {
  const today = new Date();
  return QUICK_RANGES.find((item) => {
    const range = quickRange(item.kind, today);
    return range.from === from && range.to === to;
  })?.kind || null;
}

function kpiIcon(kpi) {
  if (TOTAL_KPI_KEYS.has(kpi.key)) return <FunctionsIcon />;
  if (/пошив|крой|шв/i.test(String(kpi.label || kpi.key))) return <ContentCutIcon />;
  return <PrecisionManufacturingIcon />;
}

// ============================================================
// Мелкие переиспользуемые блоки
// ============================================================

// Панель контента — outlined Paper, как на страницах планов/Forecast Lab
function Panel({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: (t) => `1px solid ${t.palette.outlineVariant}`,
        borderRadius: 3,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function EmptyNote({ text }) {
  return (
    <Panel>
      <Typography variant="bodyMedium" color="text.secondary" sx={{ py: 2.75, px: 2, textAlign: 'center' }}>
        {text}
      </Typography>
    </Panel>
  );
}

// Единица измерения мелким шрифтом рядом с числом
function Unit({ children }) {
  return (
    <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 0.25, fontWeight: 400 }}>
      {children}
    </Typography>
  );
}

// Тонкая полоса доли (как TopList в Dashboard)
function ShareBar({ value, max, tone, sx }) {
  const theme = useTheme();
  const main = (theme.palette[tone] || theme.palette.primary).main;
  return (
    <LinearProgress
      variant="determinate"
      value={max ? Math.min(100, (Number(value) / max) * 100) : 0}
      sx={{
        height: 6,
        borderRadius: 3,
        bgcolor: alpha(main, 0.12),
        '& .MuiLinearProgress-bar': { bgcolor: main, borderRadius: 3 },
        ...sx,
      }}
    />
  );
}

// ============================================================
// KPI
// ============================================================

function KpiRow({ kpis, tones }) {
  const cards = Array.isArray(kpis) ? kpis : [];
  if (cards.length === 0) return null;
  const lg = cards.length <= 4 ? 3 : 2.4;
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((kpi) => {
        const isTotal = TOTAL_KPI_KEYS.has(kpi.key);
        return (
          <Grid item xs={6} lg={lg} key={kpi.key || kpi.label}>
            <StatCard
              title={kpi.label || kpi.key}
              value={fmtQty(kpi.value, kpi.unit)}
              subtitle={kpi.unit ? `${kpi.unit} за период` : 'за период'}
              icon={kpiIcon(kpi)}
              color={isTotal ? 'primary' : (tones.get(kpi.key) || 'primary')}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

function KpiSkeleton() {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Grid item xs={6} lg={2.4} key={i}>
          <Skeleton variant="rounded" height={148} />
        </Grid>
      ))}
    </Grid>
  );
}

// ============================================================
// Динамика по дням — по одному графику на процесс
// ============================================================

function TrendCard({ name, unit, tone, points }) {
  const theme = useTheme();
  const barColor = (theme.palette[tone] || theme.palette.primary).main;
  const many = points.length > 15;
  const total = points.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const labelSize = many ? 9 : 10;
  return (
    <Panel sx={{ height: '100%' }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          <Typography variant="titleSmall" noWrap>{name}</Typography>
          <Typography variant="bodySmall" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Σ {fmtQty(total, unit)}
          </Typography>
        </Box>
        <Box sx={{ height: 130, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} margin={{ top: 16, right: 2, left: 2, bottom: 0 }}>
              <YAxis hide domain={[0, (max) => (Number(max) > 0 ? Number(max) * 1.28 : 1)]} />
              <XAxis
                dataKey="date"
                interval={0}
                tickFormatter={(value, index) => (trendLabelVisible(index, points.length) ? shortDate(value) : '')}
                tick={{ fontSize: labelSize, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                height={22}
              />
              <ChartTooltip
                cursor={{ fill: alpha(barColor, 0.08) }}
                formatter={(value) => [`${fmtQty(value, unit)} ${unit || ''}`.trim(), name]}
                labelFormatter={(value) => longDate(value)}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
              />
              <Bar dataKey="value" fill={barColor} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false}>
                <LabelList
                  dataKey="value"
                  position="top"
                  content={({ x, y, width, value }) => {
                    if (!Number(value)) return null;
                    return (
                      <text
                        x={Number(x) + Number(width) / 2}
                        y={Number(y) - 3}
                        textAnchor="middle"
                        fill={theme.palette.text.secondary}
                        fontSize={labelSize}
                      >
                        {fmtQty(value, unit)}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Panel>
  );
}

function TrendRow({ trend, kpis, tones, from, to }) {
  const rows = Array.isArray(trend) ? trend : [];
  const series = (Array.isArray(kpis) ? kpis : []).filter((kpi) => !TOTAL_KPI_KEYS.has(kpi.key));
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="titleMedium" fontWeight={600}>Динамика по дням</Typography>
        <Typography variant="bodySmall" color="text.secondary">{formatRange(from, to)}</Typography>
      </Box>
      {rows.length === 0 || series.length === 0 ? (
        <EmptyNote text="Нет данных за период." />
      ) : (
        <Grid container spacing={2}>
          {series.map((kpi) => (
            <Grid item xs={12} sm={6} lg={3} key={kpi.key}>
              <TrendCard
                name={kpi.label || kpi.key}
                unit={kpi.unit}
                tone={tones.get(kpi.key) || 'primary'}
                points={rows.map((day) => ({
                  date: day.date,
                  value: Number(day.values?.[kpi.key] || 0),
                }))}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ============================================================
// Список моделей с полосой доли (процессы без цветов)
// ============================================================

function ItemsList({ items, unit, tone }) {
  const isMobile = useIsMobile();
  const max = Math.max(...items.map((it) => Number(it.total) || 0), 0);
  const anyWt = items.some((it) => Number(it.weight_kg) > 0);
  const cols = anyWt ? 'minmax(0,1fr) auto auto auto' : 'minmax(0,1fr) auto';
  const sumQty = items.reduce((sum, it) => sum + Number(it.total || 0), 0);
  const sumWt = items.reduce((sum, it) => sum + Number(it.weight_kg || 0), 0);
  const gap = isMobile ? 1 : 1.5;
  const num = { textAlign: 'right', whiteSpace: 'nowrap' };

  const row = (it, idx) => (
    <Box
      key={it.product_id || it.model || idx}
      sx={{
        display: 'grid',
        gridTemplateColumns: cols,
        columnGap: gap,
        alignItems: 'center',
        py: 1,
        borderBottom: (t) => `1px solid ${t.palette.outlineVariant}`,
        '&:last-of-type': { borderBottom: anyWt ? undefined : 'none' },
      }}
    >
      <Typography variant="bodyMedium" sx={{ overflowWrap: 'anywhere' }}>{it.model}</Typography>
      <Typography variant="bodyMedium" fontWeight={600} sx={num}>
        {fmtQty(it.total, unit)}
        {anyWt && <Unit>{unit}</Unit>}
      </Typography>
      {anyWt && (
        <Typography variant="bodyMedium" fontWeight={600} sx={num}>
          {fmtKg(it.weight_kg || 0)}
          <Unit>кг</Unit>
        </Typography>
      )}
      {anyWt && (
        <Typography variant="bodyMedium" color="text.secondary" sx={num}>
          {Number(it.total) ? fmtKg(Number(it.weight_kg || 0) / Number(it.total), 2) : '—'}
          <Unit>кг/шт</Unit>
        </Typography>
      )}
      <ShareBar value={it.total} max={max} tone={tone} sx={{ gridColumn: '1 / -1', mt: 0.75 }} />
    </Box>
  );

  return (
    <Panel>
      <Box sx={{ px: 2, pt: 0.5, pb: 1.25 }}>
        {items.map(row)}
        {anyWt && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: cols,
              columnGap: gap,
              alignItems: 'center',
              pt: 1.25,
              mt: 0.25,
              borderTop: (t) => `2px solid ${t.palette.outlineVariant}`,
            }}
          >
            <Typography variant="bodyMedium" fontWeight={700}>Итого</Typography>
            <Typography variant="bodyMedium" fontWeight={700} sx={num}>
              {fmtQty(sumQty, unit)}
              <Unit>{unit}</Unit>
            </Typography>
            <Typography variant="bodyMedium" fontWeight={700} sx={num}>
              {fmtKg(sumWt)}
              <Unit>кг</Unit>
            </Typography>
            <Typography variant="bodyMedium" fontWeight={700} sx={num}>
              {sumQty ? fmtKg(sumWt / sumQty, 2) : '—'}
              <Unit>кг/шт</Unit>
            </Typography>
          </Box>
        )}
      </Box>
    </Panel>
  );
}

// ============================================================
// Карточки моделей с разбивкой по цветам
// ============================================================

function ColorCards({ items, unit, tone }) {
  return (
    <Grid container spacing={2}>
      {items.map((it, idx) => {
        const colors = [...(it.by_color || [])].sort((a, b) => Number(b.qty) - Number(a.qty));
        const cmax = Math.max(...colors.map((row) => Number(row.qty) || 0), 0);
        const nColors = colors.filter((row) => colorFilterKey(row.color) !== NO_COLOR).length;
        return (
          <Grid item xs={12} sm={6} key={it.product_id || it.model || idx}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: (t) => `1px solid ${t.palette.outlineVariant}`,
                borderRadius: '12px !important',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                component="div"
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  px: 1.5,
                  '& .MuiAccordionSummary-content': { my: 1.25, ml: 0.5, minWidth: 0 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <Typography variant="titleSmall" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
                    {it.model}
                  </Typography>
                  {nColors > 0 && (
                    <Typography variant="bodySmall" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {nColors} цв.
                    </Typography>
                  )}
                  <Typography variant="titleMedium" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                    {fmtQty(it.total, unit)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
                <Stack spacing={0.75}>
                  {colors.map((row, colorIdx) => {
                    const blank = colorFilterKey(row.color) === NO_COLOR;
                    const qty = Number(row.qty) || 0;
                    return (
                      <Box key={`${colorFilterKey(row.color)}-${colorIdx}`}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="bodySmall"
                            color="text.secondary"
                            noWrap
                            sx={{ flex: 1, fontStyle: blank ? 'italic' : 'normal' }}
                          >
                            {blank ? 'без цвета' : row.color}
                          </Typography>
                          <Typography variant="bodySmall" fontWeight={600} sx={{ flexShrink: 0 }}>
                            {fmtQty(qty, unit)}
                          </Typography>
                        </Box>
                        <ShareBar value={qty} max={cmax} tone={tone} />
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ============================================================
// Секция процесса
// ============================================================

function ProcessSection({ proc, tone, isMobile, filter, onFilter, expanded, onToggle }) {
  const theme = useTheme();
  const dotColor = (theme.palette[tone] || theme.palette.primary).main;
  const items = Array.isArray(proc.items) ? proc.items : [];
  const models = uniqueModels(items);
  const colors = uniqueColors(items);
  const model = models.includes(filter?.model) ? filter.model : '';
  const colorKey = colors.includes(filter?.color) ? filter.color : '';
  const filtered = applyItemFilters(items, { model, color: colorKey });
  const unit = proc.unit || 'ед.';
  const positions = proc.items_count ?? items.length;
  let caption = `${fmtQty(proc.total, unit)} ${unit} · ${fmtInt(positions)} поз.`;
  if (Number(proc.weight_kg) > 0) caption += ` · поролон ${fmtKg(proc.weight_kg)} кг`;

  let body;
  if (!items.length) body = <EmptyNote text="Нет данных за период." />;
  else if (!filtered.length) body = <EmptyNote text="Ничего не найдено по фильтру." />;
  else if (hasAnyColor(filtered)) body = <ColorCards items={filtered} unit={unit} tone={tone} />;
  else body = <ItemsList items={filtered} unit={unit} tone={tone} />;

  const stop = (e) => e.stopPropagation();

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => onToggle(next)}
      disableGutters
      elevation={0}
      sx={{
        border: (t) => `1px solid ${t.palette.outlineVariant}`,
        borderRadius: '12px !important',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        mb: 2.5,
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse',
          px: 1.5,
          minHeight: 56,
          '& .MuiAccordionSummary-content': { my: 1.25, ml: 0.75, alignItems: 'center', minWidth: 0 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', width: '100%', pr: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flex: '0 0 auto' }} />
          <Typography component="h2" variant="titleMedium" fontWeight={600}>
            {proc.name}
          </Typography>
          <Typography variant="bodyMedium" color="text.secondary">
            {caption}
          </Typography>
          {proc.filterable && (
            <Box
              onClick={stop}
              onMouseDown={stop}
              onKeyDown={stop}
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                ml: isMobile ? 0 : 'auto',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <Autocomplete
                size="small"
                options={models}
                value={model || null}
                onChange={(_, value) => onFilter({ model: value || '', color: filter?.color || '' })}
                noOptionsText="Нет моделей"
                renderInput={(params) => <TextField {...params} placeholder="Все модели" />}
                sx={{ flex: isMobile ? '1 1 44%' : '0 0 220px', minWidth: isMobile ? 0 : 180 }}
              />
              <Autocomplete
                size="small"
                options={colors}
                value={colorKey || null}
                onChange={(_, value) => onFilter({ model: filter?.model || '', color: value || '' })}
                getOptionLabel={(opt) => colorLabel(opt)}
                noOptionsText="Нет цветов"
                renderInput={(params) => <TextField {...params} placeholder="Все цвета" />}
                sx={{ flex: isMobile ? '1 1 44%' : '0 0 180px', minWidth: isMobile ? 0 : 150 }}
              />
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        {body}
      </AccordionDetails>
    </Accordion>
  );
}

// ============================================================
// Начисления
// ============================================================

function PayrollCard({ title, tone, block, from, to }) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const rows = Array.isArray(block?.rows) ? block.rows : [];
  const unassigned = block?.unassigned;
  const showUnassigned = Number(unassigned?.money) > 0.5;
  const max = rows.reduce((m, row) => Math.max(m, Number(row.money) || 0), 0) || 1;
  const empty = rows.length === 0 && !showUnassigned;
  const dotColor = (theme.palette[tone] || theme.palette.primary).main;

  return (
    <Panel sx={{ height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.outlineVariant}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flex: '0 0 auto' }} />
          <Typography variant="titleSmall">{title}</Typography>
        </Box>
        <Typography variant="titleMedium" fontWeight={600} color="success.main" sx={{ whiteSpace: 'nowrap' }}>
          Σ {fmtMoney(block?.total_money || 0)}
        </Typography>
      </Box>
      <Box sx={{ px: 2, pt: 0.75, pb: 1.5, maxHeight: isMobile ? 'none' : 560, overflowY: 'auto' }}>
        {empty && (
          <Typography variant="bodyMedium" color="text.secondary" sx={{ py: 2.5, textAlign: 'center' }}>
            Нет начислений.
          </Typography>
        )}
        {rows.map((row, index) => {
          const clickable = Boolean(row.employee_cp_id);
          return (
            <Box
              key={row.employee_cp_id || row.name || index}
              onClick={() => {
                if (!clickable) return;
                navigate(`/my-income?employeeId=${encodeURIComponent(row.employee_cp_id)}&start=${from}&end=${to}`);
              }}
              sx={{
                py: 1,
                borderBottom: (t) => `1px solid ${t.palette.outlineVariant}`,
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="bodyMedium" sx={{ flex: 1, overflowWrap: 'anywhere', minWidth: 0 }}>
                  <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ mr: 0.75 }}>
                    {index + 1}
                  </Typography>
                  {row.name}
                  {row.ops ? (
                    <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
                      {fmtInt(row.ops)} оп.
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant="bodyMedium" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  {fmtMoney(row.money)}
                </Typography>
              </Box>
              <ShareBar value={row.money} max={max} tone="success" sx={{ mt: 0.75 }} />
            </Box>
          );
        })}
        {showUnassigned && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 1.25,
              mt: 0.75,
              borderTop: (t) => `1px dashed ${t.palette.outlineVariant}`,
            }}
          >
            <WarningAmberIcon fontSize="small" color="warning" />
            <Typography variant="bodyMedium" color="text.secondary" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
              Без исполнителя
              {unassigned.ops ? (
                <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
                  {fmtInt(unassigned.ops)} оп.
                </Typography>
              ) : null}
            </Typography>
            <Typography variant="bodyMedium" fontWeight={600} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {fmtMoney(unassigned.money)}
            </Typography>
          </Box>
        )}
      </Box>
    </Panel>
  );
}

// ============================================================
// Тулбар периода
// ============================================================

function PeriodToolbar({ from, to, onRange }) {
  const isMobile = useIsMobile();
  const active = activeQuick(from, to);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
      <Box sx={{ width: isMobile ? '100%' : 320, maxWidth: '100%' }}>
        <DateRangeField
          label="Период"
          valueFrom={from}
          valueTo={to}
          onChange={({ from: nextFrom, to: nextTo }) => onRange(nextFrom, nextTo)}
        />
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={active}
        fullWidth={isMobile}
        onChange={(_, kind) => {
          if (!kind) return;
          const range = quickRange(kind, new Date());
          onRange(range.from, range.to);
        }}
      >
        {QUICK_RANGES.map((item) => (
          <ToggleButton key={item.kind} value={item.kind} sx={{ px: 1.5, textTransform: 'none', whiteSpace: 'nowrap' }}>
            {item.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// ============================================================
// Страница
// ============================================================

export default function ProductionAnalytics() {
  const isMobile = useIsMobile();
  const user = useSelector((state) => state.auth.user);
  const canPayroll = user?.role === 'admin' || Boolean(user?.permissions?.viewEmployeeIncome);
  const location = useLocation();

  // Период и вкладка — локальное состояние, как на остальных страницах; URL читаем один раз при входе.
  // Через router (setSearchParams) не пишем: любая навигация перемонтирует страницу (ProtectedRoute
  // объявлен внутри App), и календарь терял выбор после первого клика.
  const [query, setQuery] = useState(() => resolveQuery(parseQuery(location.search), { canPayroll }));
  const { from, to } = query;
  const tab = query.tab === 'payroll' && !canPayroll ? 'stages' : query.tab;

  const [meta, setMeta] = useState(null);
  const [stages, setStages] = useState(null);
  const [stagesLoading, setStagesLoading] = useState(tab !== 'payroll');
  const [stagesError, setStagesError] = useState('');
  const [payroll, setPayroll] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState('');
  const [sectionOpen, setSectionOpen] = useState({});
  const [sectionFilters, setSectionFilters] = useState({});

  const commitQuery = (next) => {
    const resolved = resolveQuery({
      tab: next.tab ?? tab,
      from: next.from ?? from,
      to: next.to ?? to,
    }, { canPayroll });
    setQuery(resolved);
    // Deep-link ?tab&from&to поддерживаем через history без навигации роутера (см. комментарий выше)
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const search = buildQuery(resolved);
      window.history.replaceState(window.history.state, '', `${location.pathname}${search ? `?${search}` : ''}`);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    axios.get('/production-analytics/meta', { silent: true, signal: controller.signal })
      .then((res) => setMeta(res.data || null))
      .catch((err) => {
        if (!isAbort(err)) setMeta(null);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (tab === 'payroll') return undefined;
    const controller = new AbortController();
    setStages(null);
    setStagesLoading(true);
    setStagesError('');
    const scope = tab === 'sewing' ? 'sewing' : 'main';
    axios.get('/production-analytics/stages', {
      params: { from, to, scope },
      silent: true,
      signal: controller.signal,
    })
      .then((res) => {
        setStages(res.data || null);
        setStagesLoading(false);
      })
      .catch((err) => {
        if (isAbort(err)) return;
        setStages(null);
        setStagesError(errMessage(err));
        setStagesLoading(false);
      });
    return () => controller.abort();
  }, [from, to, tab]);

  useEffect(() => {
    if (tab !== 'payroll') return undefined;
    const controller = new AbortController();
    setPayroll(null);
    setPayrollLoading(true);
    setPayrollError('');
    axios.get('/production-analytics/payroll', {
      params: { from, to },
      silent: true,
      signal: controller.signal,
    })
      .then((res) => {
        setPayroll(res.data || null);
        setPayrollLoading(false);
      })
      .catch((err) => {
        if (isAbort(err)) return;
        setPayroll(null);
        setPayrollError(errMessage(err));
        setPayrollLoading(false);
      });
    return () => controller.abort();
  }, [from, to, tab]);

  const processes = Array.isArray(stages?.processes) ? stages.processes : [];
  const tones = buildToneMap(stages?.kpis);

  return (
    <Box>
      <PageHeader
        title="Производство"
        subtitle="Выполнение этапов · данные из МойСклад"
        actions={(
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {meta?.last_sync_at && (
              <Chip
                size="small"
                variant="outlined"
                icon={<SyncIcon />}
                label={`Данные обновлены: ${formatSync(meta.last_sync_at)}`}
              />
            )}
            {meta?.last_error && (
              <Tooltip title={String(meta.last_error)}>
                <Chip size="small" color="error" variant="outlined" label={String(meta.last_error)} sx={{ maxWidth: 420 }} />
              </Tooltip>
            )}
          </Stack>
        )}
      />

      <Tabs
        value={tab}
        onChange={(_, value) => commitQuery({ tab: value })}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2.5, borderBottom: (t) => `1px solid ${t.palette.outlineVariant}` }}
      >
        <Tab value="stages" label="Этапы производства" />
        <Tab value="sewing" label="Швейка" />
        {canPayroll && <Tab value="payroll" label="Начисления" />}
      </Tabs>

      <PeriodToolbar from={from} to={to} onRange={(nextFrom, nextTo) => commitQuery({ from: nextFrom, to: nextTo })} />

      {tab !== 'payroll' && (
        <>
          {stagesLoading && <LinearProgress sx={{ mb: 2 }} />}
          {stagesError && <Alert severity="error" sx={{ mb: 2 }}>{stagesError}</Alert>}
          {stagesLoading && !stages && !stagesError && <KpiSkeleton />}
          {stages && !stagesError && (
            <>
              <KpiRow kpis={stages.kpis} tones={tones} />
              <TrendRow trend={stages.trend} kpis={stages.kpis} tones={tones} from={from} to={to} />
              {processes.map((proc, index) => {
                const sectionKey = proc.key || proc.name;
                return (
                  <ProcessSection
                    key={sectionKey}
                    proc={proc}
                    tone={tones.get(proc.key) || tones.get(proc.name) || toneFor(tones.size + index)}
                    isMobile={isMobile}
                    filter={sectionFilters[sectionKey] || { model: '', color: '' }}
                    onFilter={(next) => setSectionFilters((prev) => ({ ...prev, [sectionKey]: next }))}
                    expanded={sectionOpen[sectionKey] !== false}
                    onToggle={(next) => setSectionOpen((prev) => ({ ...prev, [sectionKey]: next }))}
                  />
                );
              })}
              {processes.length === 0 && <EmptyNote text="Нет данных за период." />}
            </>
          )}
        </>
      )}

      {tab === 'payroll' && (
        <>
          {payrollLoading && <LinearProgress sx={{ mb: 2 }} />}
          {payrollError && <Alert severity="error" sx={{ mb: 2 }}>{payrollError}</Alert>}
          {payrollLoading && !payroll && !payrollError && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><Skeleton variant="rounded" height={220} /></Grid>
              <Grid item xs={12} md={6}><Skeleton variant="rounded" height={220} /></Grid>
            </Grid>
          )}
          {payroll && !payrollError && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PayrollCard title="Швейное производство" tone="secondary" block={payroll.sewing} from={from} to={to} />
              </Grid>
              <Grid item xs={12} md={6}>
                <PayrollCard title="Основное производство" tone="primary" block={payroll.main} from={from} to={to} />
              </Grid>
            </Grid>
          )}
        </>
      )}

      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ display: 'block', mt: 4, pt: 2, borderTop: (t) => `1px solid ${t.palette.outlineVariant}`, lineHeight: 1.6 }}
      >
        Источник: МойСклад, «Выполнение этапов производства». Категория — атрибут «Тип производства»,
        цвет — характеристика «Цвет», количество — producedQuantity.
        Швейка — пошив и крой; на вкладке «Этапы» её нет.
        Начисления — объём × расценка; строка «Без исполнителя» в сумму не входит.
      </Typography>
    </Box>
  );
}
